import {
  ReactNode,
  TransitionEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'

import type { FilterProps } from '@awazevr/design-system.components.filter'
import type { FilterListProps } from '@awazevr/design-system.compositions.filter-list'
import { FilterList } from '@awazevr/design-system.compositions.filter-list'
import { pxToRems } from '@awazevr/design-system.utils.react'

import { Chevron } from './Chevron'

export interface VariantFilterAccordionProps {
  additionalAriaLabel: string
  ariaLabel: string
  description?: ReactNode
  filterGroup: string
  filters: FilterProps[]
  formatShowMoreLabel: (remainingFilterCount: number) => ReactNode
  initialOpen?: boolean
  onFilterClick: FilterListProps['onFilterClick']
  onToggle?: (isOpen: boolean) => void
  selectedFilters: Array<string | number>
  showLessLabel: ReactNode
  title: ReactNode
  visibleFilterCount?: number
}

const defaultVisibleFilterCount = 6
export const VariantFilterAccordion = ({
  additionalAriaLabel,
  ariaLabel,
  description,
  filterGroup,
  filters,
  formatShowMoreLabel,
  initialOpen = false,
  onFilterClick,
  onToggle,
  selectedFilters,
  showLessLabel,
  title,
  visibleFilterCount = defaultVisibleFilterCount,
}: VariantFilterAccordionProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(initialOpen)
  const [isExpanded, setIsExpanded] = useState(initialOpen)
  const [shouldRenderHiddenFilters, setShouldRenderHiddenFilters] =
    useState(initialOpen)
  const animationFrameIdRef = useRef<number | null>(null)
  const filterListId = useId()
  const remainingFilterCount = Math.max(filters.length - visibleFilterCount, 0)
  const hasHiddenFilters = remainingFilterCount > 0
  const visibleFilters = hasHiddenFilters
    ? filters.slice(0, visibleFilterCount)
    : filters
  const hiddenFilters = hasHiddenFilters
    ? filters.slice(visibleFilterCount)
    : []

  const clearAnimationFrame = () => {
    if (animationFrameIdRef.current === null) {
      return
    }

    window.cancelAnimationFrame(animationFrameIdRef.current)
    animationFrameIdRef.current = null
  }

  const handleHiddenFiltersTransitionEnd = (
    event: TransitionEvent<HTMLDivElement>,
  ) => {
    if (event.target !== event.currentTarget || isOpen) {
      return
    }

    setShouldRenderHiddenFilters(false)
  }

  const toggleOpen = () => {
    const nextIsOpen = !isOpen

    clearAnimationFrame()

    if (nextIsOpen) {
      setShouldRenderHiddenFilters(true)
      setIsExpanded(false)

      animationFrameIdRef.current = window.requestAnimationFrame(() => {
        setIsExpanded(true)
        animationFrameIdRef.current = null
      })
    } else {
      const shouldUnmountHiddenFiltersImmediately =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches

      setIsExpanded(false)

      if (shouldUnmountHiddenFiltersImmediately) {
        setShouldRenderHiddenFilters(false)
      }
    }

    setIsOpen(nextIsOpen)
    onToggle?.(nextIsOpen)
  }

  useEffect(() => {
    return clearAnimationFrame
  }, [])

  return (
    <FilterAccordionContainer data-testid="variant-filter-accordion">
      <FilterAccordionTitle>{title}</FilterAccordionTitle>
      {description}
      <FilterListWrapper id={filterListId} aria-live="polite">
        <FilterList
          filterGroup={filterGroup}
          filters={visibleFilters}
          selectedFilters={selectedFilters}
          aria-label={ariaLabel}
          onFilterClick={onFilterClick}
        />
        {hasHiddenFilters && shouldRenderHiddenFilters && (
          <HiddenFilters
            isOpen={isExpanded}
            aria-hidden={!isOpen}
            onTransitionEnd={handleHiddenFiltersTransitionEnd}
          >
            <HiddenFiltersInner>
              <FilterList
                filterGroup={filterGroup}
                filters={hiddenFilters}
                selectedFilters={selectedFilters}
                aria-label={additionalAriaLabel}
                onFilterClick={onFilterClick}
              />
            </HiddenFiltersInner>
          </HiddenFilters>
        )}
      </FilterListWrapper>
      {hasHiddenFilters && (
        <ShowMoreToggle
          type="button"
          aria-controls={filterListId}
          aria-expanded={isOpen}
          onClick={toggleOpen}
        >
          <ToggleText>
            {isOpen ? showLessLabel : formatShowMoreLabel(remainingFilterCount)}
          </ToggleText>
          <ToggleIcon aria-hidden="true" isOpen={isOpen}>
            <Chevron />
          </ToggleIcon>
        </ShowMoreToggle>
      )}
    </FilterAccordionContainer>
  )
}

const FilterAccordionContainer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.ColorGrey100};
  color: ${({ theme }) => theme.ColorGrey800};
  padding-bottom: 12px;

  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.ColorGrey100};
  }
`

const FilterAccordionTitle = styled.h3`
  font-size: ${({ theme }) => pxToRems(theme.FontTextText300MidFontSize)};
  font-weight: ${({ theme }) => theme.FontTextText400MidFontWeight};
  line-height: ${({ theme }) => pxToRems(theme.FontTextText300MidLineHeight)};
  margin: 0;
  padding: 12px 0;
`

const FilterListWrapper = styled.div`
  padding-bottom: 0;

  ul {
    margin-bottom: 0;
  }
`

const HiddenFilters = styled.div<{ isOpen: boolean }>`
  display: grid;
  grid-template-rows: ${({ isOpen }) => (isOpen ? '1fr' : '0fr')};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  overflow: hidden;
  transition:
    grid-template-rows 200ms ease,
    opacity 160ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const HiddenFiltersInner = styled.div`
  min-height: 0;
  padding-top: 12px;
`

const ShowMoreToggle = styled.button`
  align-items: center;
  background: none;
  border: 0;
  border-radius: 4px;
  color: ${({ theme }) => theme.ColorThemeLink};
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-size: ${({ theme }) => pxToRems(theme.FontTextText200FontSize)};
  gap: 12px;
  line-height: ${({ theme }) => pxToRems(theme.FontTextText300MidLineHeight)};
  margin: 0;
  padding: 12px 0 0 0;

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 4px ${({ theme }) => theme.ColorGrey100};
  }
`

const ToggleText = styled.span`
  text-decoration: underline;
  text-underline-offset: 4px;
`

const ToggleIcon = styled.span<{ isOpen: boolean }>`
  display: inline-flex;
  transform: rotate(${({ isOpen }) => (isOpen ? '180' : '0')}deg);

  > svg {
    fill: currentColor;
    width: 13px;
  }
`
